import React, { useState } from 'react';
import { ChartFilter } from '../ChartComponents';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { SelectedItemProps } from '../MattersByStatusChart';
import {
    FormControl,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    Theme,
    createStyles,
    makeStyles,
} from '@material-ui/core';
import { ChartInfo } from '../../components/ChartInfo';
import MTooltip from '@material-ui/core/Tooltip';
import { VideoModal } from '../../components/VideoModal';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import CloseIcon from '@material-ui/icons/Close';
import { useScreenshot, createFileName } from 'use-react-screenshot';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { formatNumber } from '../../helpers/ChartHelper';
import { useCurrencySymbol } from '../../hooks/useCurrencySymbol';
import EventOutlinedIcon from '@material-ui/icons/EventOutlined';
import { RV_CHART_CLASS, RV_CHART_COLORS } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        rootLoader: {
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
        },
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        learnIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px 0 15px',
        },
        title: {
            fontSize: '16px',
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        summaryRow: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            margin: '0 0 4px 0',
        },
        total: {
            cursor: 'pointer',
            fontSize: '46px',
            fontWeight: 800,
            letterSpacing: '-1.4px',
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--rv-chart-ink)',
        },
        totalLabel: {
            marginTop: '4px',
            fontSize: '13px',
            color: 'var(--rv-chart-muted)',
        },
        conversionColumn: {
            display: 'flex',
            flexFlow: 'column',
            alignItems: 'center',
            flex: '0 0 auto',
            marginLeft: 'auto',
        },
        conversionCaption: {
            marginTop: '4px',
            fontSize: '11px',
            fontWeight: 700,
            color: 'var(--rv-chart-muted)',
            whiteSpace: 'nowrap',
        },
        conversionRing: {
            position: 'relative',
            width: '84px',
            height: '84px',
            flex: '0 0 auto',
            '& svg': {
                transform: 'rotate(-90deg)',
            },
        },
        conversionRingProgress: {
            transition: 'stroke-dashoffset 0.8s cubic-bezier(0.2, 0.8, 0.2, 1)',
            '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
            },
        },
        conversionRingLabel: {
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            display: 'flex',
            flexFlow: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            '& b': {
                fontSize: '19px',
                fontWeight: 800,
                letterSpacing: '-0.5px',
                fontVariantNumeric: 'tabular-nums',
                color: 'var(--rv-chart-ink)',
            },
            '& span': {
                fontSize: '10px',
                fontWeight: 800,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: 'var(--rv-chart-muted)',
            },
        },
        content: {
            padding: '0 15px 8px 15px',
            display: 'flex',
            flexFlow: 'column',
            flex: '1 1 auto',
            textAlign: 'left',
        },
        eyebrow: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            margin: '18px 0 8px 0',
            fontSize: '11px',
            fontWeight: 800,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            color: 'var(--rv-chart-muted)',
            '&::after': {
                content: '""',
                flex: 1,
                height: '1px',
                backgroundColor: 'var(--rv-chart-line)',
            },
            '& b': {
                fontSize: '13px',
                letterSpacing: '-0.2px',
                fontVariantNumeric: 'tabular-nums',
                color: 'var(--rv-chart-ink)',
            },
        },
        funnelRow: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            margin: '7px 0',
            fontSize: '13px',
        },
        funnelLabel: {
            display: 'flex',
            alignItems: 'center',
            width: '128px',
            flex: '0 0 auto',
            color: 'var(--rv-chart-muted)',
        },
        funnelDot: {
            width: '8px',
            height: '8px',
            marginRight: '6px',
            borderRadius: '50%',
        },
        funnelTrack: {
            flex: 1,
            height: '8px',
            borderRadius: '999px',
            overflow: 'hidden',
            backgroundColor: 'var(--rv-chart-track)',
        },
        funnelFill: {
            height: '100%',
            borderRadius: '999px',
            transition: 'width 0.8s cubic-bezier(0.2, 0.8, 0.2, 1)',
            '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
            },
        },
        funnelValue: {
            width: '40px',
            textAlign: 'right',
            fontWeight: 800,
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--rv-chart-ink)',
        },
        tiles: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px',
        },
        tile: {
            minWidth: 0,
            padding: '10px 12px',
            border: '1px solid var(--rv-chart-line)',
            borderRadius: '10px',
            background: 'linear-gradient(180deg, #fff 0%, #fafbff 100%)',
        },
        tileFullWidth: {
            gridColumn: '1 / -1',
        },
        tileLabel: {
            fontSize: '11px',
            fontWeight: 800,
            letterSpacing: '0.06em',
            lineHeight: 1.3,
            textTransform: 'uppercase',
            color: 'var(--rv-chart-muted)',
        },
        tileValue: {
            marginTop: '2px',
            fontSize: '22px',
            fontWeight: 800,
            letterSpacing: '-0.6px',
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--rv-chart-ink)',
        },
        tileCaption: {
            fontSize: '12px',
            color: 'var(--rv-chart-muted)',
        },
        pointer: {
            cursor: 'pointer',
        },
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
            padding: '0 15px',
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            position: 'relative',
        },
    }),
);

enum OptionsEnum {
    LAST_3_MONTHS = 'LAST_3_MONTHS',
    LAST_6_MONTHS = 'LAST_6_MONTHS',
    LAST_12_MONTHS = 'LAST_12_MONTHS',
    ALL_TIME = 'ALL_TIME',
}

interface EventsAndCampaignSummaryChartChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    infoUrl: string | null;
}

interface ChartState  {
    options: OptionsEnum;
}

export const EventsAndCampaignSummaryChart = (props: EventsAndCampaignSummaryChartChartProps) => {
    const classes = useStyles();
    // tslint:disable-next-line: no-any
    const entireChartRef = React.useRef<any>();
    const currencyQuery = useCurrencySymbol();

    const [state, setState] = useState<ChartState>({
        options: OptionsEnum.LAST_3_MONTHS,
    });

    const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);

    const takeScreenShot = useScreenshot({
        type: 'image/jpeg',
        quality: 1.0,
    })[1];

    const eventAndCampaignSummaryChartQuery = useQuery<EventAndCampaignSummaryChartQueryData, EventAndCampaignSummaryChartQueryParams>(EventAndCampaignSummaryChartQuery, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid
        }
    });

    const onToggleVideo = () => {
        setIsVideoModalOpen(!isVideoModalOpen);
    };

    const onMaximize = () => {
        if (props.onMaximize) {
            props.onMaximize();
        }
    };

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart(`isEnquiryHealthChartOpen`);
        }
    };

    const onDownload =
        (name: string, extension: string = 'jpg') =>
            // tslint:disable-next-line: no-any
            (image: any) => {
                const a = document.createElement('a');
                a.href = image;
                a.download = createFileName(extension, name);
                a.click();
            };

    // tslint:disable-next-line: no-any
    const onDownloadScreenshot = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
        setTimeout(() => {
            takeScreenShot(entireChartRef.current).then(onDownload(`CRM Enquires Data Health`));
            // tslint:disable-next-line: align
        }, 500);
    };

    const chartControlComponent = () => {
        return (
            <>
                <IconButtonWrapper aria-label="close">
                    <div className={`${classes.helpIcon} rv-chart-help`}>
                        <ChartInfo
                            description={props.description}
                            fontSize={props.showAllRecords ? 'large' : 'medium'}
                        // className={classes.helpIcon}
                        />
                        {props.infoUrl && (
                            <MTooltip title="Learn more">
                                <IconButton className={classes.learnIcon} aria-label="maximize" onClick={onToggleVideo} size={props.showAllRecords ? 'medium' : 'small'}>
                                    <VideoLibraryOutlinedIcon />
                                </IconButton>
                            </MTooltip>
                        )}
                    </div>
                    {(props.showAllRecords || props.showScreenshot) && (
                        <IconButton
                            size={props.showAllRecords ? 'medium' : 'small'}
                            onClick={onDownloadScreenshot}
                        >
                            <GetAppOutlinedIcon
                                color="primary"
                                fontSize={props.showAllRecords ? 'large' : 'medium'}
                            />
                        </IconButton>
                    )}
                    {!props.showAllRecords && props.onMaximize && (
                        <MTooltip title="Expand chart">
                            <IconButton aria-label="maximize" onClick={onMaximize} size="small">
                                <AspectRatioIcon />
                            </IconButton>
                        </MTooltip>
                    )}
                    <IconButton aria-label="close" onClick={onCloseChart} size={props.showAllRecords ? 'medium' : 'small'}>
                        <CloseIcon fontSize={props.showAllRecords ? 'large' : 'medium'} />
                    </IconButton>
                </IconButtonWrapper>
            </>
        );
    };

    if (eventAndCampaignSummaryChartQuery.loading) {
        return (
            <div className={`${classes.rootLoader} ${RV_CHART_CLASS}`}>
                {chartControlComponent()}
                <ChartSkeleton variant="bars" />
            </div>
        );
    }

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                options: (event.target as HTMLInputElement).value as OptionsEnum,
            };
        });
    };

    const getGroupedData = () => {
        const {    
            eventsAndCampaignSummaryAllTime,
            eventsAndCampaignSummaryLast3Months,
            eventsAndCampaignSummaryLast6Months,
            eventsAndCampaignSummaryLast12Months
        } = eventAndCampaignSummaryChartQuery.data!.statistics.lead.eventAndCampaignSummaryByGroup;

        if (state.options === OptionsEnum.LAST_3_MONTHS) {
            return eventsAndCampaignSummaryLast3Months;
        } else if (state.options === OptionsEnum.LAST_6_MONTHS) {
            return eventsAndCampaignSummaryLast6Months;
        } else if (state.options === OptionsEnum.LAST_12_MONTHS) {
            return eventsAndCampaignSummaryLast12Months;
        } else {
            return eventsAndCampaignSummaryAllTime;
        }
    }

    const getTitle = () => {
        if (state.options === OptionsEnum.LAST_3_MONTHS) {
            return 'started in the last 3 months';
        } else if (state.options === OptionsEnum.LAST_6_MONTHS) {
            return 'started in the last 6 months';
        } else if (state.options === OptionsEnum.LAST_12_MONTHS) {
            return 'started in the last 12 months';
        } else {
            return 'All time';
        }
    }

    if (currencyQuery.loading || eventAndCampaignSummaryChartQuery.loading) {
        return (
            <div className={`${classes.loadingWrapper} ${RV_CHART_CLASS}`}>
                {chartControlComponent()}
                <ChartSkeleton variant="bars" />
            </div>
        );
    }
    
    const symbol = currencyQuery.data?.options.currencySymbol.symbol || '$';

    const groupedData = getGroupedData();

    const renderCount = (amount: number) => Math.round(amount).toLocaleString();

    const renderCurrency = (amount: number) => `${symbol}${formatNumber(amount, 0)}`;

    // How much of the enquiries generated each outcome is (a sliver stays visible when the count is small but not zero).
    const getFunnelWidth = (count: number | undefined) => {
        const generatedCount = groupedData?.linkedCount || 0;
        if (!count || !generatedCount) {
            return '0%';
        }
        return `${Math.min(100, Math.max(1.5, (count / generatedCount) * 100))}%`;
    };

    const renderFunnelRow = (label: string, fullLabel: string, count: number | undefined, color: string) => (
        <div className={classes.funnelRow} title={fullLabel}>
            <span className={classes.funnelLabel}>
                <span className={classes.funnelDot} style={{ backgroundColor: color }} />
                {label}
            </span>
            <div className={classes.funnelTrack}>
                <div className={classes.funnelFill} style={{ width: getFunnelWidth(count), backgroundColor: color }} />
            </div>
            <span className={classes.funnelValue}>
                <AnimatedNumber value={count ?? 0} format={renderCount} />
            </span>
        </div>
    );

    const renderTile = (label: string, fullLabel: string, value: number | undefined, format: (amount: number) => string, caption?: string, isFullWidth?: boolean) => (
        <div className={`${classes.tile}${isFullWidth ? ` ${classes.tileFullWidth}` : ''}`} title={fullLabel}>
            <div className={classes.tileLabel}>{label}</div>
            <div className={classes.tileValue}>
                <AnimatedNumber value={value ?? 0} format={format} />
            </div>
            {caption && <div className={classes.tileCaption}>{caption}</div>}
        </div>
    );

    const conversionRingRadius = 34;
    const conversionRingLength = 2 * Math.PI * conversionRingRadius;
    const conversionRate = Math.min(100, Math.max(0, groupedData?.conversionRate ?? 0));

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}`} ref={entireChartRef}>
            {isVideoModalOpen && props.infoUrl && <VideoModal open={isVideoModalOpen} onClose={onToggleVideo} url={props.infoUrl} />}
            {chartControlComponent()}
            <div className={classes.titleContainer}>
                <ChartHeading icon={<EventOutlinedIcon />} title="Events & Campaigns" subtitle={getTitle()} />
            </div>
            <div className={classes.content}>
                <div className={classes.summaryRow}>
                    <div>
                        <div className={classes.total}>
                            <AnimatedNumber value={groupedData?.totalCount || 0} format={renderCount} />
                        </div>
                        <div className={classes.totalLabel}>events &amp; campaigns</div>
                    </div>
                    <div className={classes.conversionColumn} title="Conversion rate of enquiries proceeding">
                        <div className={classes.conversionRing}>
                            <svg width="84" height="84" viewBox="0 0 84 84">
                                <circle cx="42" cy="42" r={conversionRingRadius} fill="none" stroke="#f2f4fa" strokeWidth="9" />
                                <circle
                                    className={classes.conversionRingProgress}
                                    cx="42"
                                    cy="42"
                                    r={conversionRingRadius}
                                    fill="none"
                                    stroke={RV_CHART_COLORS.green}
                                    strokeWidth="9"
                                    strokeLinecap="round"
                                    strokeDasharray={conversionRingLength}
                                    strokeDashoffset={conversionRingLength * (1 - conversionRate / 100)}
                                />
                            </svg>
                            <div className={classes.conversionRingLabel}>
                                <b>
                                    <AnimatedNumber value={groupedData?.conversionRate ?? 0} format={(amount) => `${formatNumber(amount, 0)}%`} />
                                </b>
                            </div>
                        </div>
                        <div className={classes.conversionCaption}>Conversion rate</div>
                    </div>
                </div>

                <div className={classes.eyebrow} title="Enquiries generated">
                    <span>Enquiries generated</span>
                    <b>
                        <AnimatedNumber value={groupedData?.linkedCount ?? 0} format={renderCount} />
                    </b>
                </div>
                {renderFunnelRow('Still active', 'Enquiries still active', groupedData?.activeCount, RV_CHART_COLORS.primary)}
                {renderFunnelRow('Proceeding', 'Enquiries proceeding', groupedData?.proceedingCount, RV_CHART_COLORS.green)}
                {renderFunnelRow('Not proceeding', 'Enquiries not proceeding', groupedData?.notProceedingCount, RV_CHART_COLORS.red)}
                <div className={classes.tiles} style={{ marginTop: 12 }}>
                    {renderTile('Billing from new matters', 'Billing from new matters generated', groupedData?.feesBilled, renderCurrency)}
                    {renderTile('Avg per new matter', 'Average billing per new matter generated', groupedData?.averageBillingPerMatterGenerated, renderCurrency)}
                </div>

                <div className={classes.eyebrow}>Budgets</div>
                <div className={classes.tiles}>
                    {renderTile('With budgets', 'Events and Campaigns with budgets', groupedData?.eventsWithBudgetCount, renderCount, 'events & campaigns')}
                    {renderTile('Total budgets', 'Total of all budgets', groupedData?.budgetedCost, renderCurrency)}
                    {renderTile('Enquiries generated', 'Enquiries generated by campaigns with budgets', groupedData?.enquiriesWithBudgetedEventsCount, renderCount, 'by budgeted campaigns')}
                    {renderTile('Avg cost / enquiry', 'Average cost per enquiry', groupedData?.averageCostPerEnquiry, renderCurrency)}
                    {renderTile('Total billing from those enquiries', 'Total billing generated from enquiries', groupedData?.proceedingEnquiriesWithBudgetedEventsBilling, renderCurrency, undefined, true)}
                </div>
            </div>
            <FormControl component="div">
                <RadioGroup name="last" value={state.options} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={OptionsEnum.LAST_3_MONTHS} control={<Radio color="primary" />} label="Last 3 months" />

                    <FormControlLabel value={OptionsEnum.LAST_6_MONTHS} control={<Radio color="primary" />} label="Last 6 months" />
                    <FormControlLabel value={OptionsEnum.LAST_12_MONTHS} control={<Radio color="primary" />} label="Last 12 months" />
                    <FormControlLabel value={OptionsEnum.ALL_TIME} control={<Radio color="primary" />} label="All time" />
                </RadioGroup>
            </FormControl>
        </div>
    );
};

type EventAndCampaignSummaryChartQueryParams = {
    entityType?: number;
    entityGuid?: string;
};

type EventAndCampaignSummaryChartQueryData = {
    statistics: Statistics;
};

export type Data = {
    statistics: Statistics;
}

export type Statistics = {
    lead: Lead;
}

export type Lead = {
    eventAndCampaignSummaryByGroup: EventAndCampaignSummaryByGroup;
}

export type EventAndCampaignSummaryByGroup = {
    eventsAndCampaignSummaryAllTime:      EventAndCampaignSummaryByGroupField | null;
    eventsAndCampaignSummaryLast3Months:  EventAndCampaignSummaryByGroupField | null;
    eventsAndCampaignSummaryLast6Months:  EventAndCampaignSummaryByGroupField | null;
    eventsAndCampaignSummaryLast12Months: EventAndCampaignSummaryByGroupField | null;
}


export type EventAndCampaignSummaryByGroupField = {
    totalCount: number;
    linkedCount: number;
    activeCount: number;
    notProceedingCount: number;
    proceedingCount: number;
    conversionRate: number;
    feesBilled: number;
    eventsWithBudgetCount: number;
    budgetedCost: number;
    enquiriesWithBudgetedEventsCount: number;
    proceedingEnquiriesWithBudgetedEvents: number;
    proceedingEnquiriesWithBudgetedEventsBilling: number;
    averageCostPerEnquiry: number;
    averageBillingPerMatterGenerated: number;
}

const EventAndCampaignSummaryChartQuery = gql`
    # Write your query or mutation here
    fragment eventAndCampaignSummaryGrouped on EventAndCampaignSummaryGrouped {
        totalCount
        linkedCount
        activeCount
        notProceedingCount
        proceedingCount
        conversionRate
        feesBilled
        eventsWithBudgetCount
        budgetedCost
        enquiriesWithBudgetedEventsCount
        proceedingEnquiriesWithBudgetedEvents
        proceedingEnquiriesWithBudgetedEventsBilling
        averageCostPerEnquiry
        averageBillingPerMatterGenerated
    }

    query EventAndCampaignSummaryChartData($entityType: Int, $entityGuid: String) {
        statistics {
            lead {
                eventAndCampaignSummaryByGroup (entityType: $entityType, entityGuid: $entityGuid) {
                    eventsAndCampaignSummaryAllTime{
                        ...eventAndCampaignSummaryGrouped
                    }
                    eventsAndCampaignSummaryLast3Months{
                        ...eventAndCampaignSummaryGrouped
                    }
                    eventsAndCampaignSummaryLast6Months{
                        ...eventAndCampaignSummaryGrouped
                    }
                    eventsAndCampaignSummaryLast12Months{
                        ...eventAndCampaignSummaryGrouped
                    }
                }
            }
        }
    }
`;
